import {
  IonButton,
  IonButtons,
  IonCheckbox,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonItem,
  IonModal,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { close } from 'ionicons/icons';
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Machine,
  useListMachinesByUserQuery,
} from '../../../api/graphql/generated';
import { ObjectList } from '../../object/object-list';
import { PlanBilledBy } from '../subscription.types';

interface LicenseExplorerProps {
  billedBy: PlanBilledBy;
  title: string;
  presentingElement?: HTMLElement | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirmation: (entityIds: string[]) => void;
  autoSelectedEntityIds?: string[];
}

const LicenseExplorer = (props: LicenseExplorerProps): ReactElement => {
  const {
    autoSelectedEntityIds,
    isOpen,
    onClose,
    onConfirmation,
    presentingElement,
    title,
  } = props;
  const { i18n } = useTranslation();

  const explorerModalRef = useRef<HTMLIonModalElement>(null);
  const hasCalledOnClosed = useRef<boolean>(false);

  const [selectedEntityIds, setSelectedEntityIds] = useState<string[]>([]);

  const selectedEntityIdsRef = useRef<string[]>(selectedEntityIds);
  selectedEntityIdsRef.current = selectedEntityIds;

  useEffect(() => {
    setSelectedEntityIds(autoSelectedEntityIds ?? []);
  }, [autoSelectedEntityIds]);

  const onCloseHandler = useCallback(() => {
    onClose();
    hasCalledOnClosed.current = true;
  }, [onClose]);

  const onEntitySelect = useCallback(
    ({ entityId, checked }: { entityId: string; checked: boolean }) => {
      if (checked) {
        setSelectedEntityIds([...selectedEntityIdsRef.current, entityId]);
      } else {
        setSelectedEntityIds(
          selectedEntityIdsRef.current.filter((id) => id !== entityId)
        );
      }
    },
    []
  );

  return (
    <IonModal
      isOpen={isOpen}
      ref={explorerModalRef}
      canDismiss={true}
      presentingElement={presentingElement ?? undefined}
      onWillPresent={() => {
        hasCalledOnClosed.current = false;
      }}
      onDidDismiss={() => {
        if (hasCalledOnClosed.current === false) {
          onCloseHandler();
        }
      }}
    >
      <IonHeader>
        <IonToolbar>
          <IonButtons slot={'start'}>
            <IonButton
              onClick={() => {
                onCloseHandler();
              }}
            >
              <IonIcon icon={close} size="large" />
            </IonButton>
          </IonButtons>
          <IonTitle>{title}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <ObjectList
          query={{
            useQuery: useListMachinesByUserQuery,
            input: {
              active: true,
            },
            queryOptions: {
              fetchPolicy: 'cache-and-network',
              nextFetchPolicy: 'cache-first',
            },
          }}
          noResults={i18n.t('common:no_results_found')}
          rowRenderer={({ item }: { item: Machine }) => {
            const checked = selectedEntityIds?.includes(item.id) ?? false;
            const serialNumber = item.state?.runtimeInfo?.serialNumber;

            let label = item.name;

            if (serialNumber) {
              label += ` - ${serialNumber}`;
            }

            return (
              <IonItem key={item.id}>
                <IonCheckbox
                  justify="space-between"
                  checked={checked}
                  onIonChange={(e) => {
                    onEntitySelect({
                      entityId: item.id,
                      checked: e.detail.checked,
                    });
                  }}
                >
                  {label}
                </IonCheckbox>
              </IonItem>
            );
          }}
        />
      </IonContent>
      <IonFooter>
        <IonToolbar>
          <IonButtons slot="primary">
            <IonButton
              onClick={() => {
                onConfirmation(selectedEntityIds);
              }}
            >
              {i18n.t('common:update')}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonFooter>
    </IonModal>
  );
};

export { LicenseExplorer };
